// Account deletions — the admin's approval queue.
//
// A customer asks to delete their account from their dashboard (squish-frontend
// Billing & Account). squish-backend holds the request for 14 days, and only
// while nothing on the account is live, then parks it as waiting on approval.
// This page lists every open request; the ones waiting on an admin carry an
// Approve button.
//
// Approving runs squish-backend's erasure engine. It checks everything again
// and refuses on its own if anything came back, but once it runs it cannot be
// undone. So the admin types the customer's email before the button turns on,
// and squish-crm-backend checks it against the same request before it acts.
// The approver's email becomes the erasure record's actor.
//
// Admin only: the nav entry (App.jsx ADMIN_ITEMS) and both server routes.
// Rules in src/lib/accountDeletions.js.

import React, { useCallback, useEffect, useState } from 'react';
import { listAccountDeletions, approveAccountDeletion } from '../api';
import {
  statusMeta, holdReason, boardRows, waitingCount, emailMatches, approveOutcome, shortDate,
} from '../lib/accountDeletions';

const TONE = {
  red:   { color: 'var(--accent2)', background: 'rgba(239,68,68,0.12)' },
  amber: { color: 'var(--badge-yellow)', background: 'rgba(234,179,8,0.12)' },
  quiet: { color: 'var(--text2)', background: 'var(--bg3)' },
};

const btn = {
  background: 'var(--bg3)', color: 'var(--text2)', border: '1px solid var(--border)',
  borderRadius: 8, padding: '6px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
};
const dangerBtn = {
  ...btn, color: 'var(--accent2)', borderColor: 'var(--accent2)', background: 'rgba(239,68,68,0.10)', fontWeight: 600,
};

export default function Deletions() {
  const [requests, setRequests] = useState(null);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(null); // uid with the confirm box open
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(null);             // uid while an approval runs
  const [notice, setNotice] = useState(null);         // { uid, email, ok, text }

  const load = useCallback(async () => {
    try {
      setRequests(await listAccountDeletions());
      setError('');
    } catch (e) {
      setError(e.status === 403 ? 'Account deletions are for admins.' : e.message);
      setRequests((prev) => prev || []);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  function openConfirm(uid) {
    setNotice(null);
    setTyped('');
    setConfirming(uid);
  }

  async function approve(row) {
    if (!emailMatches(typed, row.email)) return;
    setBusy(row.uid);
    setNotice(null);
    try {
      const result = await approveAccountDeletion(row.uid, { confirmEmail: typed });
      const o = approveOutcome(result);
      setNotice({ uid: row.uid, email: row.email, ok: o.ok, text: o.text });
      setConfirming(null);
      setTyped('');
    } catch (e) {
      setNotice({ uid: row.uid, email: row.email, ok: false, text: e.message });
    }
    setBusy(null);
    load();
  }

  const rows = boardRows(requests);
  const waiting = waitingCount(requests);
  // A deleted request leaves the list; its outcome still needs saying.
  const orphanNotice = notice && !rows.some((r) => r.uid === notice.uid) ? notice : null;

  return (
    <div style={{ padding: 32, maxWidth: 980 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8, gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Account deletions</h1>
        <button onClick={load} style={btn}>↻ Refresh</button>
      </div>
      <div style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 20, maxWidth: 680, lineHeight: 1.5 }}>
        Customers who asked to delete their account. After 14 days, with nothing live left on the account,
        each one waits here for you. Approving erases the account for good. It can't be undone.
      </div>

      {error && <div style={{ marginBottom: 16, color: 'var(--accent2)', fontSize: 13 }}>{error}</div>}
      {orphanNotice && (
        <div style={{ marginBottom: 16, fontSize: 13, color: orphanNotice.ok ? 'var(--accent3)' : 'var(--accent2)' }}>
          {orphanNotice.email}: {orphanNotice.text}
        </div>
      )}

      {requests === null ? (
        <div style={{ padding: 20, color: 'var(--text2)', fontSize: 13 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 20, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, color: 'var(--text2)', fontSize: 14 }}>
          ✓ No open deletion requests.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 8 }}>
            {waiting === 0 ? 'Nothing waiting on you.' : `${waiting} waiting on you.`}
          </div>
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
            {rows.map((r, i) => {
              const meta = statusMeta(r.status);
              const tone = TONE[meta.tone] || TONE.quiet;
              const isBusy = busy === r.uid;
              const note = notice?.uid === r.uid ? notice : null;
              const reason = r.status === 'blocked' ? holdReason(r.blockedReason) : '';
              return (
                <div key={r.uid} style={{ padding: '14px 18px', borderTop: i ? '1px solid var(--border)' : 'none', opacity: isBusy ? 0.6 : 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ ...tone, fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap' }}>{meta.label}</span>
                    <span style={{ flex: '1 1 220px', minWidth: 0, fontSize: 14, fontWeight: 600, color: 'var(--text)', overflowWrap: 'anywhere' }}>{r.email || r.uid}</span>
                    <span style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                      Asked {shortDate(r.requestedAt)} · due {shortDate(r.scheduledFor)}
                    </span>
                    {meta.approvable && confirming !== r.uid && (
                      <button data-write onClick={() => openConfirm(r.uid)} style={dangerBtn}>Approve deletion…</button>
                    )}
                  </div>
                  {reason && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 6 }}>{reason}</div>}
                  {meta.approvable && confirming === r.uid && (
                    <div data-write style={{ marginTop: 12, padding: 12, borderRadius: 8, background: 'var(--bg3)', border: '1px solid var(--border)' }}>
                      <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 8 }}>
                        This erases <strong>{r.email}</strong>: their sign-in and their data, except the records
                        the law makes us keep (like invoices). It can't be undone.
                      </div>
                      <label htmlFor={`del-confirm-${r.uid}`} style={{ display: 'block', fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>
                        Type the customer's email to confirm
                      </label>
                      <input
                        id={`del-confirm-${r.uid}`} value={typed} onChange={(e) => setTyped(e.target.value)}
                        autoComplete="off" spellCheck={false}
                        style={{ width: '100%', maxWidth: 360, boxSizing: 'border-box', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' }}
                      />
                      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                        <button
                          onClick={() => approve(r)} disabled={isBusy || !emailMatches(typed, r.email)}
                          style={{ ...dangerBtn, opacity: isBusy || !emailMatches(typed, r.email) ? 0.5 : 1, cursor: isBusy || !emailMatches(typed, r.email) ? 'default' : 'pointer' }}
                        >
                          {isBusy ? 'Deleting…' : 'Delete permanently'}
                        </button>
                        <button onClick={() => { setConfirming(null); setTyped(''); }} disabled={isBusy} style={btn}>Cancel</button>
                      </div>
                    </div>
                  )}
                  {note && (
                    <div style={{ marginTop: 8, fontSize: 12, color: note.ok ? 'var(--accent3)' : 'var(--accent2)' }}>{note.text}</div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
